import { SiweMessage } from 'siwe';
import { getAddress } from 'viem';

/**
 * SIWE (Sign-In With Ethereum) Utilities
 * Based on EIP-4361 specification
 */

/**
 * Build SIWE message for signing
 * @param {string} address - Wallet address
 * @param {string} nonce - Nonce for this session
 * @param {string} domain - Domain (e.g., 'hubid.app')
 * @param {string} origin - Origin (e.g., 'https://hubid.app')
 * @param {number} chainId - Chain ID (1 for mainnet, 11155111 for sepolia)
 * @returns {SiweMessage} SIWE message object
 */
export const buildSiweMessage = (address, nonce, domain, origin, chainId = 1) => {
  const message = new SiweMessage({
    domain,
    address: getAddress(address), // Normalize address
    statement: 'Sign in with Ethereum to HubID',
    uri: origin,
    version: '1',
    chainId,
    nonce,
    issuedAt: new Date().toISOString(),
  });

  return message;
};

/**
 * Convert SIWE message to string format for signing
 * @param {SiweMessage} message - SIWE message object
 * @returns {string} Formatted message string
 */
export const messageToString = (message) => {
  return message.prepareMessage();
};

/**
 * Verify SIWE message signature
 * @param {string} message - SIWE message string
 * @param {string} signature - Signature from wallet
 * @param {Object} provider - Ethereum provider (from wagmi)
 * @returns {Promise<boolean>} True if signature is valid
 */
export const verifySiweSignature = async (message, signature, provider) => {
  try {
    const siweMessage = new SiweMessage(message);
    
    // Verify the message
    const result = await siweMessage.verify({
      signature,
      domain: siweMessage.domain,
      nonce: siweMessage.nonce,
      time: new Date().toISOString(),
    });

    return result.success;
  } catch (error) {
    console.error('Error verifying SIWE signature:', error);
    return false;
  }
};

/**
 * Parse SIWE message from string
 * @param {string} messageString - SIWE message string
 * @returns {SiweMessage} Parsed SIWE message
 */
export const parseSiweMessage = (messageString) => {
  try {
    return new SiweMessage(messageString);
  } catch (error) {
    console.error('Error parsing SIWE message:', error);
    throw error;
  }
};

/**
 * Get domain from current environment
 * @returns {string} Domain name
 */
export const getDomain = () => {
  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }
  return import.meta.env.VITE_APP_DOMAIN || 'hubid.app';
};

/**
 * Get origin from current environment
 * @returns {string} Origin URL
 */
export const getOrigin = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return import.meta.env.VITE_APP_URL || 'https://hubid.app';
};

/**
 * Validate SIWE message fields
 * @param {SiweMessage} message - SIWE message to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export const validateSiweMessage = (message) => {
  const errors = [];

  if (!message.domain) {
    errors.push('Domain is required');
  }

  if (!message.address) {
    errors.push('Address is required');
  }

  if (!message.nonce || message.nonce.length < 8) {
    errors.push('Nonce must be at least 8 characters');
  }

  if (!message.uri) {
    errors.push('URI is required');
  }

  if (message.version !== '1') {
    errors.push('Version must be 1');
  }

  if (!message.chainId) {
    errors.push('Chain ID is required');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

